import { useState } from "react";
import { ACCOUNT_DELETE_CONFIRMATION_PHRASE } from "@panelgenie/contracts";
import {
  Banner,
  Button,
  Card,
  Heading,
  Input,
  Modal,
} from "@panelgenie/shared-ui";

/**
 * Settings → Account delete affordance.
 *
 * The user taps "Delete my account". A typed-confirmation modal
 * gates the irreversible action; the user must type the phrase
 * `ACCOUNT_DELETE_CONFIRMATION_PHRASE` exactly. The parent route
 * forwards the typed value to the server, which re-validates it.
 *
 * Pure presentation. Parent owns the tRPC mutation + the
 * post-success Clerk sign-out + redirect to the confirmation
 * surface.
 */
export type AccountDeleteState =
  | { readonly kind: "idle" }
  | { readonly kind: "deleting" }
  | { readonly kind: "deleted" }
  | { readonly kind: "error"; readonly message: string };

export type AccountDeletePanelProps = {
  readonly state: AccountDeleteState;
  readonly onDelete: (typedPhrase: string) => void;
  readonly slaCopy: string;
};

export function AccountDeletePanel({
  state,
  onDelete,
  slaCopy,
}: AccountDeletePanelProps) {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const isBusy = state.kind === "deleting";
  return (
    <Card aria-label="account-delete" className="border-danger/40 bg-danger/5">
      <Card.Header>
        <Heading level="h2" className="text-danger">
          Delete my account
        </Heading>
      </Card.Header>
      <Card.Body className="space-y-3">
        <p className="text-base text-fg">
          Permanently delete your account and every site, room, panel,
          device, warning, and photo you own. This action cannot be
          undone.
        </p>
        <p className="text-sm text-mute">{slaCopy}</p>
        <Button
          variant="danger"
          onClick={() => setIsModalOpen(true)}
          disabled={isBusy || state.kind === "deleted"}
          loading={isBusy}
          aria-label="delete-my-account"
        >
          {isBusy ? "Deleting your account…" : "Delete my account"}
        </Button>

        {state.kind === "error" ? (
          <Banner variant="danger" role="alert">
            {state.message}
          </Banner>
        ) : null}
      </Card.Body>

      <ConfirmDeleteModal
        open={isModalOpen}
        slaCopy={slaCopy}
        onCancel={() => setIsModalOpen(false)}
        onConfirm={(typedPhrase) => {
          setIsModalOpen(false);
          onDelete(typedPhrase);
        }}
      />
    </Card>
  );
}

type ConfirmDeleteModalProps = {
  readonly open: boolean;
  readonly slaCopy: string;
  readonly onCancel: () => void;
  readonly onConfirm: (typedPhrase: string) => void;
};

function ConfirmDeleteModal({
  open,
  slaCopy,
  onCancel,
  onConfirm,
}: ConfirmDeleteModalProps) {
  const [typed, setTyped] = useState("");
  const normalized = typed.trim().toLowerCase();
  const isPhraseValid = normalized === ACCOUNT_DELETE_CONFIRMATION_PHRASE;
  return (
    <Modal
      open={open}
      onClose={onCancel}
      aria-label="confirm-delete-dialog"
      title={<span className="text-danger">Delete your account?</span>}
      footer={
        <>
          <Button variant="ghost" onClick={onCancel}>
            Cancel
          </Button>
          <Button
            variant="danger"
            onClick={() => onConfirm(typed)}
            disabled={!isPhraseValid}
            aria-label="confirm-delete"
          >
            Yes, delete my account
          </Button>
        </>
      }
    >
      <p className="text-base text-fg">
        This permanently removes every site, room, panel, device, warning,
        photo, and share link on your account. The action cannot be
        undone.
      </p>
      <p className="mt-3 text-sm text-mute">{slaCopy}</p>
      <label className="block mt-4 text-base font-medium text-fg">
        Type{" "}
        <code className="rounded bg-subtle px-1.5 py-0.5 font-mono text-sm">
          {ACCOUNT_DELETE_CONFIRMATION_PHRASE}
        </code>{" "}
        to confirm:
        <Input
          type="text"
          value={typed}
          onChange={(e) => setTyped(e.target.value)}
          aria-label="delete-confirmation-input"
          autoFocus
          className="mt-2 font-mono"
        />
      </label>
    </Modal>
  );
}
